import React, { memo, useEffect, useMemo, useState } from 'react';
import { AIModel } from '@/types';
import { useAppStore } from '@/store';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Bot,
  Check,
  ChevronDown,
  Cloud,
  FlaskConical,
  Globe,
  Laptop,
  Search,
  Server,
  Sparkles,
  Monitor,
  Cpu,
  Brain,
  Wrench,
  Languages,
  Shield,
  Zap,
  ArrowUpRight,
  Eye,
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useOsLicense } from '@/hooks/supabase/useOsLicense';
import Gemma4OnnxConsentDialog from '@/components/models/Gemma4OnnxConsentDialog';
import ModelLogo from '@/components/models/ModelLogo';
import {
  appendGemma4OnnxModel,
  isGemma4OnnxModelId,
  loadGemma4OnnxAcknowledged,
} from '@/lib/gemma4Onnx';
import { filterEnabledModels } from '@/lib/modelSettings';
import {
  formatContextLength,
  formatSizeGb,
  getBestOverallModels,
  getModelBadges,
  getModelCompatibilityWarning,
  getModelDescriptionLine,
  getModelProviderLabel,
  getModelQualityLabel,
  getModelRuntimeLabel,
  getModelSizeLabel,
  getModelTaskScore,
  getTabModels,
  getTopModelsForTask,
  isFrontierModel,
  isOnDeviceModel,
  matchesModelSearch,
  type ModelBrowserTab,
  type ModelTask,
} from '@/components/models/modelPresentation';
import { resolveModelIconSrc } from '@/lib/modelIcons';

interface ModelSelectorProps {
  models: AIModel[];
  selectedModelId: string;
  onModelChange: (modelId: string) => void;
  disabled?: boolean;
  preferBrowser?: boolean;
  fullWidth?: boolean;
}

type SortMode = 'recommended' | 'name' | 'size' | 'context';
type RuntimeFilter = 'all' | 'browser' | 'onnx' | 'frontier' | 'ollama-local' | 'ollama-remote';
type ModelCardInteraction = 'select' | 'preview';

type ModelCardProps = {
  model: AIModel;
  selected: boolean;
  compact?: boolean;
  warning?: string;
  interaction?: ModelCardInteraction;
  onSelect: (modelId: string) => void;
  onPreview: (modelId: string) => void;
};

const TASK_FILTERS: Array<{ id: ModelTask | 'all'; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'all', label: 'All', icon: Sparkles },
  { id: 'coding', label: 'Coding', icon: Cpu },
  { id: 'reasoning', label: 'Reasoning', icon: Brain },
  { id: 'tool-use', label: 'Tools', icon: Wrench },
  { id: 'vision', label: 'Vision', icon: Eye },
  { id: 'multilingual', label: 'Languages', icon: Languages },
  { id: 'privacy', label: 'Private', icon: Shield },
  { id: 'fast', label: 'Fast', icon: Zap },
];

const RUNTIME_FILTERS: Array<{ id: RuntimeFilter; label: string }> = [
  { id: 'all', label: 'All runtimes' },
  { id: 'frontier', label: 'Frontier' },
  { id: 'browser', label: 'Browser' },
  { id: 'onnx', label: 'ONNX' },
  { id: 'ollama-local', label: 'Ollama local' },
  { id: 'ollama-remote', label: 'Ollama remote' },
];

const getProviderIcon = (model: AIModel) => {
  if (model.provider === 'ollama' && model.source === 'remote') return <Cloud className="h-4 w-4 text-blue-600" />;
  if (model.provider === 'ollama' && model.source === 'local') return <Laptop className="h-4 w-4 text-emerald-600" />;
  if (model.provider === 'local') return <Laptop className="h-4 w-4 text-emerald-600" />;
  if (model.provider === 'transformers') return <FlaskConical className="h-4 w-4 text-amber-500" />;
  if (model.provider === 'maplept') return <Sparkles className="h-4 w-4 text-violet-500" />;
  if (model.provider === 'claude') return <Sparkles className="h-4 w-4 text-orange-500" />;
  if (['openai', 'gemini', 'deepseek', 'cohere'].includes(model.provider)) return <Bot className="h-4 w-4 text-muted-foreground" />;
  return <Server className="h-4 w-4 text-muted-foreground" />;
};

const getAvatar = (model: AIModel, sizeClass = 'h-9 w-9') => (
  <ModelLogo
    src={resolveModelIconSrc(model)}
    alt={model.name}
    sizeClass={sizeClass}
    fallback={getProviderIcon(model)}
  />
);

const getSummaryText = (model: AIModel) => {
  if (model.description) return model.description;

  const bestFor = model.use_cases?.slice(0, 2).join(' and ');
  const runtime = getModelRuntimeLabel(model).toLowerCase();
  const size = formatSizeGb(model.size_gb) || getModelSizeLabel(model)?.toLowerCase();

  return [
    model.family ? `${model.family} model` : model.name,
    bestFor ? `best for ${bestFor}` : null,
    runtime ? `runs via ${runtime}` : null,
    size ? `around ${size}` : null,
  ]
    .filter(Boolean)
    .join(' • ');
};

const getStrengths = (model: AIModel) => {
  const strengths: string[] = [];

  if (model.supports_reasoning) strengths.push('Strong for reasoning, analysis, and harder multi-step prompts.');
  if (model.supports_tools || model.supports_json) strengths.push('Good fit for tools, structured output, and agent-style workflows.');
  if (model.supports_vision) strengths.push('Can handle image-aware or multimodal tasks.');
  if (model.supports_multilingual || (model.languages?.length || 0) > 1) strengths.push('Better choice for multilingual conversations and translation tasks.');
  if (isOnDeviceModel(model)) strengths.push('Runs locally or on-device for better privacy and lower cloud dependence.');
  if (isFrontierModel(model)) strengths.push('Frontier hosted model with stronger top-end quality and larger system capacity.');
  if (typeof model.context_length === 'number' && model.context_length >= 64000) strengths.push('Large context window helps with long documents and bigger conversations.');
  if ((model.size_gb ?? 99) < 1) strengths.push('Very lightweight and usually faster to start than larger models.');
  if (getModelQualityLabel(model) === 'High quality') strengths.push('Higher-fidelity weights favor quality over startup speed.');

  return strengths.slice(0, 4);
};

const getConsiderations = (model: AIModel) => {
  const considerations: string[] = [];

  if (isOnDeviceModel(model) && (model.size_gb ?? 0) >= 4) considerations.push('Larger local models may need more GPU memory and can take longer to initialize.');
  if (isFrontierModel(model)) considerations.push('Hosted models rely on network access and may consume paid provider credits.');
  if (!model.supports_tools && !model.supports_json) considerations.push('Not the best pick if you need reliable JSON or tool calling.');
  if (!model.supports_vision) considerations.push('Text-first model; image-heavy tasks may work better with a vision-capable option.');
  if (getModelQualityLabel(model) === 'Fast') considerations.push('Speed-optimized quantization may trade away some quality for lower resource usage.');

  return considerations.slice(0, 3);
};

const getCapabilitySummary = (model: AIModel) => {
  const items: string[] = [];
  if (model.supports_reasoning) items.push('Reasoning');
  if (model.supports_tools) items.push('Tools');
  if (model.supports_json) items.push('JSON');
  if (model.supports_vision) items.push('Vision');
  if (model.supports_multilingual) items.push('Languages');
  return items;
};

const FilterChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Button
    type="button"
    variant={active ? 'default' : 'outline'}
    size="sm"
    className="h-8 rounded-full px-3"
    onClick={onClick}
  >
    {children}
  </Button>
);

const ModelCard = ({ model, selected, compact = false, warning, interaction = 'select', onSelect, onPreview }: ModelCardProps) => {
  const badges = getModelBadges(model);
  const handlePrimaryAction = () => {
    if (interaction === 'preview') {
      onPreview(model.id);
      return;
    }
    onSelect(model.id);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handlePrimaryAction}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handlePrimaryAction();
        }
      }}
      className={`w-full cursor-pointer rounded-xl border p-3 text-left transition hover:border-primary/50 hover:bg-accent/40 ${selected ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border bg-background'}`}
    >
      <div className="flex items-start gap-3">
        {getAvatar(model, compact ? 'h-8 w-8' : 'h-9 w-9')}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate font-medium text-foreground">{model.name}</p>
                {selected ? <Check className="h-4 w-4 text-primary" /> : null}
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {getModelRuntimeLabel(model)} • {getModelProviderLabel(model)}
              </p>

            </div>
            {!compact ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={(event) => {
                  event.stopPropagation();
                  onPreview(model.id);
                }}
              >
                {interaction === 'preview' ? 'Preview' : 'Details'}
              </Button>
            ) : null}
          </div>

          {!compact ? <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{getModelDescriptionLine(model)}</p> : null}

          <div className="mt-2 flex flex-wrap gap-1.5">

            {badges.map((badge) => (
              <Badge key={badge} variant="secondary" className="text-[10px]">
                {badge}
              </Badge>
            ))}
            {formatContextLength(model.context_length) ? <Badge variant="outline" className="text-[10px]">{formatContextLength(model.context_length)}</Badge> : null}
            {formatSizeGb(model.size_gb) ? <Badge variant="outline" className="text-[10px]">{formatSizeGb(model.size_gb)}</Badge> : null}
            {getModelQualityLabel(model) ? <Badge variant="outline" className="text-[10px]">{getModelQualityLabel(model)}</Badge> : null}
          </div>

          {warning ? <p className="mt-2 text-xs text-amber-600">{warning}</p> : null}
        </div>
      </div>
    </div>
  );
};

const ModelDetailPanel = ({
  model,
  onSelect,
  selected,
}: {
  model: AIModel | undefined;
  selected: boolean;
  onSelect: (modelId: string) => void;
}) => {
  if (!model) {
    return (
      <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
        Choose a model to see more detail.
      </div>
    );
  }

  const strengths = getStrengths(model);
  const considerations = getConsiderations(model);
  const capabilitySummary = getCapabilitySummary(model);

  return (
    <div className="rounded-2xl border bg-card p-5">
      <div className="flex items-start gap-4">
        {getAvatar(model, 'h-12 w-12')}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="truncate text-xl font-semibold text-foreground">{model.name}</h3>
                {selected ? <Badge className="rounded-full">Selected</Badge> : null}
                {model.is_official ? <Badge variant="secondary" className="rounded-full">Official</Badge> : null}
              </div>
            </div>
            <Button size="sm" onClick={() => onSelect(model.id)}>
              {selected ? 'Using this model' : 'Use this model'}
            </Button>
          </div>

          <p className="mt-4 text-sm leading-6 text-muted-foreground">{getSummaryText(model)}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {getModelBadges(model).map((badge) => (
              <Badge key={badge} variant="secondary" className="rounded-full">{badge}</Badge>
            ))}
            {model.family ? <Badge variant="outline" className="rounded-full">{model.family}</Badge> : null}
            {getModelSizeLabel(model) ? <Badge variant="outline" className="rounded-full">{getModelSizeLabel(model)}</Badge> : null}
            {getModelQualityLabel(model) ? <Badge variant="outline" className="rounded-full">{getModelQualityLabel(model)}</Badge> : null}
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 text-sm xl:grid-cols-4">
        <div className="rounded-xl bg-muted/50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Runtime</p>
          <p className="mt-1 font-medium">{getModelRuntimeLabel(model)}</p>
        </div>
        <div className="rounded-xl bg-muted/50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Context</p>
          <p className="mt-1 font-medium">{formatContextLength(model.context_length) || 'Unknown'}</p>
        </div>
        <div className="rounded-xl bg-muted/50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Size</p>
          <p className="mt-1 font-medium">{formatSizeGb(model.size_gb) || getModelSizeLabel(model) || 'Unknown'}</p>
        </div>
        <div className="rounded-xl bg-muted/50 p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Quantization</p>
          <p className="mt-1 font-medium">{model.quantization || 'N/A'}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-1">
        <div className="rounded-xl border bg-background/60 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">What it is good at</p>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {strengths.length > 0 ? strengths.map((item) => (
              <li key={item} className="flex gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                <span>{item}</span>
              </li>
            )) : (
              <li className="text-sm text-muted-foreground">Solid general-purpose model with no specific strengths highlighted yet.</li>
            )}
          </ul>
        </div>

      </div>

      {model.use_cases?.length || capabilitySummary.length ? (
        <div className="mt-5 rounded-xl border bg-background/60 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Best for</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {model.use_cases?.map((item) => (
              <Badge key={item} variant="outline" className="rounded-full">{item}</Badge>
            ))}
            {capabilitySummary.map((item) => (
              <Badge key={item} variant="secondary" className="rounded-full">{item}</Badge>
            ))}
          </div>
        </div>
      ) : null}

      {model.quantization_description ? (
        <div className="mt-5 rounded-xl border bg-background p-4 text-sm text-muted-foreground">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Why this variant exists</p>
          {model.quantization_description}
        </div>
      ) : null}
    </div>
  );
};

const sortModels = (models: AIModel[], sortMode: SortMode, activeTask: ModelTask | 'all') => {
  const values = [...models];

  if (sortMode === 'name') {
    return values.sort((a, b) => a.name.localeCompare(b.name));
  }

  if (sortMode === 'size') {
    return values.sort((a, b) => (a.size_gb ?? Number.MAX_SAFE_INTEGER) - (b.size_gb ?? Number.MAX_SAFE_INTEGER));
  }

  if (sortMode === 'context') {
    return values.sort((a, b) => (b.context_length ?? 0) - (a.context_length ?? 0));
  }

  const task = activeTask === 'all' ? 'general' : activeTask;
  const scoredGroups = getTopModelsForTask(values, task, values.length);
  return scoredGroups;
};

const matchesRuntimeFilter = (model: AIModel, runtimeFilter: RuntimeFilter) => {
  if (runtimeFilter === 'all') return true;
  if (runtimeFilter === 'frontier') return isFrontierModel(model);
  if (runtimeFilter === 'browser') return model.runtime === 'webllm' || model.provider === 'local';
  if (runtimeFilter === 'onnx') return model.runtime === 'onnx' || model.provider === 'transformers';
  if (runtimeFilter === 'ollama-local') return model.provider === 'ollama' && model.source === 'local';
  if (runtimeFilter === 'ollama-remote') return model.provider === 'ollama' && model.source === 'remote';
  return true;
};

const getRuntimeGroup = (model: AIModel) => {
  if (model.provider === 'ollama' && model.source === 'remote') return 'ollama-remote';
  if (model.provider === 'ollama' && model.source === 'local') return 'ollama-local';
  if (model.provider === 'local') return 'local';
  if (model.provider === 'transformers') return 'onnx';
  if (model.provider === 'maplept') return 'frontier';
  if (['openai', 'claude', 'gemini', 'deepseek', 'cohere'].includes(model.provider)) return 'browser';
  return 'frontier';
};

const ModelBrowserBody = ({
  models,
  selectedModelId,
  activeTab,
  setActiveTab,
  taskFilter,
  setTaskFilter,
  runtimeFilter,
  setRuntimeFilter,
  sortMode,
  setSortMode,
  searchQuery,
  setSearchQuery,
  detailModelId,
  setDetailModelId,
  onSelectModel,
}: {
  models: AIModel[];
  selectedModelId: string;
  activeTab: ModelBrowserTab;
  setActiveTab: (value: ModelBrowserTab) => void;
  taskFilter: ModelTask | 'all';
  setTaskFilter: (value: ModelTask | 'all') => void;
  runtimeFilter: RuntimeFilter;
  setRuntimeFilter: (value: RuntimeFilter) => void;
  sortMode: SortMode;
  setSortMode: (value: SortMode) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  detailModelId: string;
  setDetailModelId: (value: string) => void;
  onSelectModel: (modelId: string) => void;
}) => {
  const detailModel = useMemo(
    () => models.find((model) => model.id === detailModelId) || models.find((model) => model.id === selectedModelId) || models[0],
    [detailModelId, models, selectedModelId]
  );

  const filteredModels = useMemo(() => {
    const byTab = getTabModels(models, activeTab);
    const bySearch = searchQuery.trim()
      ? byTab.filter((model) => matchesModelSearch(model, searchQuery))
      : byTab;
    const byRuntime = bySearch.filter((model) => matchesRuntimeFilter(model, runtimeFilter));
    const byTask = taskFilter === 'all'
      ? byRuntime
      : byRuntime.filter((model) => getModelTaskScore(model, taskFilter) > 0);

    return sortModels(byTask, sortMode, taskFilter);
  }, [activeTab, models, runtimeFilter, searchQuery, sortMode, taskFilter]);

  const recommendationGroups = useMemo(
    () => [
      { title: 'Best overall', task: 'general' as ModelTask },
      { title: 'Best for coding', task: 'coding' as ModelTask },
      { title: 'Best for reasoning', task: 'reasoning' as ModelTask },
      { title: 'Best for tool use', task: 'tool-use' as ModelTask },
      { title: 'Best for multilingual work', task: 'multilingual' as ModelTask },
      { title: 'Best on-device privacy', task: 'privacy' as ModelTask },
      { title: 'Fastest lightweight picks', task: 'fast' as ModelTask },
    ],
    []
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row md:items-stretch">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="pl-9"
              placeholder="Search by model, family, provider, capability..."
            />
          </div>

          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ModelBrowserTab)} className="flex min-h-0 flex-1 flex-col">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="recommended">Recommended</TabsTrigger>
              <TabsTrigger value="all">All models</TabsTrigger>
              <TabsTrigger value="local">Local</TabsTrigger>
              <TabsTrigger value="frontier">Frontier</TabsTrigger>
            </TabsList>

            <div className="mt-3 flex flex-wrap gap-2">
              {TASK_FILTERS.map((filter) => (
                <FilterChip key={filter.id} active={taskFilter === filter.id} onClick={() => setTaskFilter(filter.id)}>
                  <filter.icon className="mr-1 h-3.5 w-3.5" />
                  {filter.label}
                </FilterChip>
              ))}
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              {(['recommended', 'name', 'size', 'context'] as SortMode[]).map((value) => (
                <FilterChip key={value} active={sortMode === value} onClick={() => setSortMode(value)}>
                  Sort: {value}
                </FilterChip>
              ))}
            </div>

            <TabsContent value="recommended" className="mt-4 min-h-0 flex-1">
              <ScrollArea className="h-full pr-2">
                {searchQuery.trim() ? (
                  <div className="space-y-3 pb-4">
                    <div>
                      <h3 className="font-medium text-foreground">Search results</h3>
                      <p className="text-xs text-muted-foreground">
                        Showing matches for "{searchQuery.trim()}".
                      </p>
                    </div>
                    <div className="grid gap-2">
                      {filteredModels.map((model) => (
                        <ModelCard
                          key={model.id}
                          model={model}
                          selected={model.id === detailModel?.id}
                          compact
                          interaction="preview"
                          warning={taskFilter === 'all' ? undefined : getModelCompatibilityWarning(model, taskFilter)}
                          onSelect={onSelectModel}
                          onPreview={setDetailModelId}
                        />
                      ))}
                      {filteredModels.length === 0 ? (
                        <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
                          No models match "{searchQuery.trim()}".
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 pb-4">
                    {recommendationGroups.map((group) => {
                      const modelsForGroup = group.task === 'general'
                        ? getBestOverallModels(getTabModels(models, activeTab), 3)
                        : getTopModelsForTask(getTabModels(models, activeTab), group.task, 3);
                      return (
                        <div key={group.title} className="space-y-2">
                          <div>
                            <h3 className="font-medium text-foreground">{group.title}</h3>
                            <p className="text-xs text-muted-foreground">Select a model to preview its summary before switching.</p>
                          </div>
                          <div className="grid gap-2">
                            {modelsForGroup.map((model) => (
                              <ModelCard
                                key={model.id}
                                model={model}
                                selected={model.id === detailModel?.id}
                                compact
                                interaction="preview"
                                warning={getModelCompatibilityWarning(model, group.task)}
                                onSelect={onSelectModel}
                                onPreview={setDetailModelId}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="all" className="mt-4 min-h-0 flex-1">
              <ScrollArea className="h-full pr-2">
                <div className="grid gap-2 pb-4">
                  {filteredModels.map((model) => (
                    <ModelCard
                      key={model.id}
                      model={model}
                      selected={model.id === detailModel?.id}
                      interaction="preview"
                      warning={taskFilter === 'all' ? undefined : getModelCompatibilityWarning(model, taskFilter)}
                      onSelect={onSelectModel}
                      onPreview={setDetailModelId}
                    />
                  ))}
                  {filteredModels.length === 0 ? (
                    <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
                      No models match these filters.
                    </div>
                  ) : null}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="local" className="mt-4 min-h-0 flex-1">
              <ScrollArea className="h-full pr-2">
                <div className="grid gap-2 pb-4">
                  {filteredModels.map((model) => (
                    <ModelCard
                      key={model.id}
                      model={model}
                      selected={model.id === detailModel?.id}
                      interaction="preview"
                      warning={taskFilter === 'all' ? undefined : getModelCompatibilityWarning(model, taskFilter)}
                      onSelect={onSelectModel}
                      onPreview={setDetailModelId}
                    />
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="frontier" className="mt-4 min-h-0 flex-1">
              <ScrollArea className="h-full pr-2">
                <div className="grid gap-2 pb-4">
                  {filteredModels.map((model) => (
                    <ModelCard
                      key={model.id}
                      model={model}
                      selected={model.id === detailModel?.id}
                      interaction="preview"
                      warning={taskFilter === 'all' ? undefined : getModelCompatibilityWarning(model, taskFilter)}
                      onSelect={onSelectModel}
                      onPreview={setDetailModelId}
                    />
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <div className="hidden min-h-0 w-full max-w-md shrink-0 md:block">
        <ScrollArea className="h-full pr-2">
          <div className="pb-4">
            <ModelDetailPanel model={detailModel} selected={detailModel?.id === selectedModelId} onSelect={onSelectModel} />
          </div>
        </ScrollArea>
      </div>

    </div>
  );
};

const ModelSelector: React.FC<ModelSelectorProps> = memo(({ models: availableModelsProp, selectedModelId, onModelChange, disabled = false, preferBrowser = false, fullWidth = false }) => {
  const { user } = useAppStore();
  const { hasProOrEnterprise, loading: osLicenseLoading } = useOsLicense();
  const isMobile = useIsMobile();
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingGemmaModelId, setPendingGemmaModelId] = useState<string | null>(null);
  const [gemmaAcknowledged, setGemmaAcknowledged] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ModelBrowserTab>('all');
  const [taskFilter, setTaskFilter] = useState<ModelTask | 'all'>('all');
  const [runtimeFilter, setRuntimeFilter] = useState<RuntimeFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('recommended');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailModelId, setDetailModelId] = useState(selectedModelId);

  useEffect(() => {
    void loadGemma4OnnxAcknowledged().then(setGemmaAcknowledged);
  }, []);

  const availableModels = useMemo(() => {
    const withOnDevice = filterEnabledModels(appendGemma4OnnxModel(Array.isArray(availableModelsProp) ? availableModelsProp : []));

    if (withOnDevice.length === 0) return [];
    if (!user) {
      return withOnDevice.filter(
        (model) => model.tier === 'free' || isOnDeviceModel(model) || model.provider === 'ollama'
      );
    }

    return withOnDevice;
  }, [availableModelsProp, user]);

  useEffect(() => {
    const selectedAvailable = availableModels.some((model) => model.id === selectedModelId);
    if (availableModels.length > 0 && (!selectedAvailable || !selectedModelId)) {
      onModelChange(availableModels[0].id);
    } else if (availableModels.length === 0 && selectedModelId) {
      onModelChange('');
    }
  }, [availableModels, onModelChange, selectedModelId]);

  useEffect(() => {
    setDetailModelId(selectedModelId);
  }, [selectedModelId]);

  useEffect(() => {
    if (!disabled) return;
    setPopoverOpen(false);
    setBrowserOpen(false);
    setConsentOpen(false);
  }, [disabled]);

  const selectedModelData = useMemo(
    () => availableModels.find((model) => model.id === selectedModelId),
    [availableModels, selectedModelId]
  );

  const previewModelData = useMemo(
    () => availableModels.find((model) => model.id === detailModelId) || selectedModelData,
    [availableModels, detailModelId, selectedModelData]
  );

  const openBrowser = (modelId?: string) => {
    if (disabled) return;
    if (modelId) {
      setDetailModelId(modelId);
    }
    setPopoverOpen(false);
    setBrowserOpen(true);
  };

  const handleModelSelection = (modelId: string) => {
    if (disabled) return;
    if (isGemma4OnnxModelId(modelId) && !gemmaAcknowledged) {
      setPendingGemmaModelId(modelId);
      setConsentOpen(true);
      return;
    }

    onModelChange(modelId);
    setDetailModelId(modelId);
    setPopoverOpen(false);
    setBrowserOpen(false);
  };

  const handleConsentConfirmed = () => {
    setGemmaAcknowledged(true);
    if (disabled) return;
    if (pendingGemmaModelId) {
      onModelChange(pendingGemmaModelId);
      setDetailModelId(pendingGemmaModelId);
      setPopoverOpen(false);
      setBrowserOpen(false);
    }
  };

  const quickSuggestions = useMemo(() => {
    const pickFirst = (predicate: (model: AIModel) => boolean) => availableModels.find(predicate);

    const curated = [
      pickFirst((model) => model.provider === 'claude' && /claude sonnet 4\.6/i.test(model.name)),
      pickFirst((model) => /qwen 3 8b/i.test(model.name)),
      pickFirst((model) => /deepseek r1 distill qwen 7b/i.test(model.name)),
    ].filter((model): model is AIModel => Boolean(model));

    if (curated.length === 3) {
      return curated;
    }

    const fallback = getTopModelsForTask(availableModels, 'general', availableModels.length);
    const seen = new Set(curated.map((model) => model.id));

    for (const model of fallback) {
      if (seen.has(model.id)) continue;
      curated.push(model);
      seen.add(model.id);
      if (curated.length === 3) break;
    }

    return curated;
  }, [availableModels]);

  const triggerWidthClass = fullWidth || isMobile ? 'w-full' : 'w-[260px]';
  const trigger = (
    <Button
      variant="outline"
      className={`${triggerWidthClass} min-w-0 justify-between font-normal`}
      disabled={disabled || availableModels.length === 0}
      onClick={preferBrowser ? () => openBrowser(selectedModelId || detailModelId) : undefined}
    >
      <div className="flex min-w-0 items-center gap-2">
        {selectedModelData ? (
          <>
            {getAvatar(selectedModelData, 'h-5 w-5')}
            <div className="min-w-0 text-left">
              <p className="truncate text-sm font-medium">{selectedModelData.name}</p>
              <p className="truncate text-xs text-muted-foreground">{getModelRuntimeLabel(selectedModelData)} • {getModelProviderLabel(selectedModelData)}</p>
            </div>
          </>
        ) : (
          <>
            <Monitor className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">Select a model</span>
          </>
        )}
      </div>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </Button>
  );

  const popoverBody = (
    <div className="grid gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
      <div className="space-y-4">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-start gap-3">
            {previewModelData ? getAvatar(previewModelData, 'h-10 w-10') : <Globe className="h-5 w-5 text-muted-foreground" />}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-foreground">{previewModelData?.name || 'No model selected'}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {previewModelData ? `${getModelRuntimeLabel(previewModelData)} • ${getModelProviderLabel(previewModelData)}` : 'Choose the best model for this task.'}
              </p>

            </div>
          </div>

          {previewModelData ? (
            <>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {getModelBadges(previewModelData).slice(0, 4).map((badge) => (
                  <Badge key={badge} variant="secondary" className="text-[10px]">{badge}</Badge>
                ))}
                {formatContextLength(previewModelData.context_length) ? (
                  <Badge variant="outline" className="text-[10px]">{formatContextLength(previewModelData.context_length)}</Badge>
                ) : null}
              </div>
              <p className="mt-3 line-clamp-4 text-xs text-muted-foreground">
                {getSummaryText(previewModelData)}
              </p>
              <Button className="mt-3 w-full" size="sm" onClick={() => previewModelData && handleModelSelection(previewModelData.id)}>
                {previewModelData.id === selectedModelId ? 'Using this model' : 'Use this model'}
              </Button>
            </>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Quick picks</p>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openBrowser(previewModelData?.id || selectedModelId)}>
              Browse all
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="grid gap-2">
            {quickSuggestions.map((model) => (
              <button
                key={model.id}
                type="button"
                onClick={() => setDetailModelId(model.id)}
                className={`rounded-lg border px-3 py-2 text-left transition hover:border-primary/50 hover:bg-accent/40 ${model.id === (previewModelData?.id || selectedModelId) ? 'border-primary bg-primary/5' : 'border-border bg-background'}`}
              >
                <div className="flex items-center gap-2">
                  {getAvatar(model, 'h-7 w-7')}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{model.name}</p>
                    <p className="truncate text-[11px] text-muted-foreground">{getModelRuntimeLabel(model)} • {getModelProviderLabel(model)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="min-w-0 space-y-3">
        <div className="flex flex-col gap-3 rounded-xl border bg-card p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} className="pl-9" placeholder="Search models" />
              </div>
              <Button
                size="sm"
                className="shrink-0 bg-violet-600 text-white hover:bg-violet-600/90 disabled:bg-violet-600/40 disabled:text-white/80"
                onClick={() => openBrowser(previewModelData?.id || detailModelId || selectedModelId)}
                disabled={disabled || !hasProOrEnterprise || osLicenseLoading}
                title={hasProOrEnterprise ? 'Open the detailed Pro model browser' : 'Requires MapleOS Pro or Enterprise'}
              >
                Pro
              </Button>

            </div>
            <div className="flex flex-wrap gap-2">
              <FilterChip active={activeTab === 'recommended'} onClick={() => setActiveTab('recommended')}>Recommended</FilterChip>
              <FilterChip active={activeTab === 'all'} onClick={() => setActiveTab('all')}>All</FilterChip>
              <FilterChip active={activeTab === 'local'} onClick={() => setActiveTab('local')}>Local</FilterChip>
              <FilterChip active={activeTab === 'frontier'} onClick={() => setActiveTab('frontier')}>Frontier</FilterChip>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {sortModels(
              getTabModels(availableModels, activeTab).filter((model) => !searchQuery || matchesModelSearch(model, searchQuery)),
              'recommended',
              taskFilter
            )
              .slice(0, 4)
              .map((model) => (
                <ModelCard
                  key={model.id}
                  model={model}
                  compact
                  interaction="preview"
                  selected={model.id === (previewModelData?.id || selectedModelId)}
                  onSelect={handleModelSelection}
                  onPreview={setDetailModelId}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isMobile || preferBrowser ? (
        <Button
          variant="outline"
          className={`${triggerWidthClass} min-w-0 justify-between font-normal`}
          onClick={() => openBrowser(selectedModelId || detailModelId)}
          disabled={disabled || availableModels.length === 0}
        >
          <div className="flex min-w-0 items-center gap-2">
            {selectedModelData ? (
              <>
                {getAvatar(selectedModelData, 'h-5 w-5')}
                <div className="min-w-0 text-left">
                  <p className="truncate text-sm font-medium">{selectedModelData.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{getModelRuntimeLabel(selectedModelData)} • {getModelProviderLabel(selectedModelData)}</p>
                </div>
              </>
            ) : (
              <>
                <Monitor className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">Select a model</span>
              </>
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      ) : (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent align="start" side="bottom" sideOffset={8} collisionPadding={16} className="w-[min(92vw,760px)] p-4">
            {popoverBody}
          </PopoverContent>
        </Popover>
      )}

      {isMobile ? (
        <Sheet open={browserOpen} onOpenChange={setBrowserOpen}>
          <SheetContent side="right" className="w-full max-w-none overflow-hidden px-4">
            <SheetHeader>
              <SheetTitle>Model browser</SheetTitle>
              <SheetDescription>Filter, compare, and choose the best model for the task.</SheetDescription>
            </SheetHeader>
            <div className="mt-4 flex min-h-0 flex-1 flex-col">
              <ModelBrowserBody
                models={availableModels}
                selectedModelId={selectedModelId}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                taskFilter={taskFilter}
                setTaskFilter={setTaskFilter}
                runtimeFilter={runtimeFilter}
                setRuntimeFilter={setRuntimeFilter}
                sortMode={sortMode}
                setSortMode={setSortMode}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                detailModelId={detailModelId}
                setDetailModelId={setDetailModelId}
                onSelectModel={handleModelSelection}
              />
              <div className="mt-4 md:hidden">
                <ModelDetailPanel
                  model={availableModels.find((model) => model.id === detailModelId) || selectedModelData}
                  selected={detailModelId === selectedModelId}
                  onSelect={handleModelSelection}
                />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={browserOpen} onOpenChange={setBrowserOpen}>
          <DialogContent className="max-h-[88vh] max-w-6xl overflow-hidden p-0">
            <div className="flex h-full max-h-[88vh] flex-col p-6">
              <DialogHeader>
                <DialogTitle>Model browser</DialogTitle>
                <DialogDescription>
                  Preview a model first, then switch when the summary looks right for your task.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 flex min-h-0 flex-1 flex-col">
                <ModelBrowserBody
                  models={availableModels}
                  selectedModelId={selectedModelId}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  taskFilter={taskFilter}
                  setTaskFilter={setTaskFilter}
                  runtimeFilter={runtimeFilter}
                  setRuntimeFilter={setRuntimeFilter}
                  sortMode={sortMode}
                  setSortMode={setSortMode}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  detailModelId={detailModelId}
                  setDetailModelId={setDetailModelId}
                  onSelectModel={handleModelSelection}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Gemma4OnnxConsentDialog
        open={consentOpen}
        onOpenChange={setConsentOpen}
        onConfirmed={handleConsentConfirmed}
      />
    </>
  );
});

ModelSelector.displayName = 'ModelSelector';

export default ModelSelector;